<template>
  <base-tooltip
    v-if="isTruncated"
    :text="text"
    :location="location"
    :open-delay="openDelay"
  >
    <template #activator="{ props: activator }">
      <span :ref="setEl" v-bind="activator" class="truncated-text">{{
        text
      }}</span>
    </template>
  </base-tooltip>
  <span v-else :ref="setEl" class="truncated-text">{{ text }}</span>
</template>

<script setup lang="ts">
  import { ref, onBeforeUnmount, watch, nextTick } from 'vue';
  import { BaseTooltip } from '@deltax/vuetify-wrapper-components';

  const props = withDefaults(
    defineProps<{ text: string; location?: string; openDelay?: number }>(),
    { location: 'top', openDelay: 500 }
  );

  const el = ref<HTMLElement | null>(null);
  const isTruncated = ref(false);
  let observer: ResizeObserver | null = null;

  // The text is clipped when its full content is wider than the box it
  // renders in. Only then is the tooltip worth showing.
  function measure() {
    const node = el.value;
    if (node) isTruncated.value = node.scrollWidth > node.clientWidth;
  }

  // The visible span swaps between the plain and tooltip-wrapped versions
  // as `isTruncated` flips, so track whichever node is currently mounted.
  function setEl(node: unknown) {
    el.value = (node as HTMLElement) ?? null;
    observer?.disconnect();
    if (el.value) {
      measure();
      observer = new ResizeObserver(measure);
      observer.observe(el.value);
    }
  }

  onBeforeUnmount(() => observer?.disconnect());

  watch(
    () => props.text,
    () => nextTick(measure)
  );
</script>

<style scoped>
  .truncated-text {
    display: block;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
</style>
